import { z } from 'zod';
import type { DriveSourceKind, FileProvider } from '@prisma/client';
import type { FileTextReadReason } from '@/logic/content/file-text-cache.js';
import type { NormalizedDrive, NormalizedDriveItem } from '@/services/drives/contract.js';
import type { SearchMatch } from './content-catalog.js';
import { jsonValueSchema } from './scripts.js';
import { FileTextCacheReason } from './enums.js';
import { SEARCH_QUERY_FIELD_DESCRIPTION } from './search.js';

export const DRIVE_ROOT_ROLES = ['PROJECTS', 'LIBRARY', 'MIRROR'] as const;
export type DriveRootRoleValue = (typeof DRIVE_ROOT_ROLES)[number];

export const DRIVE_ROOT_ROLE_EXPLANATIONS: Record<DriveRootRoleValue, string> = {
  PROJECTS: 'One project anchor per sub-folder',
  LIBRARY: 'Tag files by sub-folder',
  MIRROR: 'Reference only',
};

export const DRIVE_ROOT_ITEM_ALIAS = 'root';

export const DRIVE_SOURCE_KINDS = ['MICROSOFT', 'RELAY', 'GOOGLE'] as const;
export type DriveSourceKindValue = (typeof DRIVE_SOURCE_KINDS)[number];
export const DRIVE_SOURCE_KIND_VALUES_MATCH_PRISMA: [DriveSourceKind] extends [DriveSourceKindValue] ? true : never = true;

export const DRIVE_SOURCE_STATUSES = ['PENDING', 'ACTIVE', 'REVOKED'] as const;
export type DriveSourceStatusValue = (typeof DRIVE_SOURCE_STATUSES)[number];

export const FILE_PROVIDERS = ['VIVPORT', 'SHAREPOINT', 'SERVER_SHARE', 'GOOGLE_DRIVE'] as const;
export type FileProviderValue = (typeof FILE_PROVIDERS)[number];
export const FILE_PROVIDERS_MATCH_PRISMA: [FileProvider] extends [FileProviderValue] ? true : never = true;

export const driveRootFiltersSchema = z
  .object({
    modifiedAfter: z.string().trim().min(1).nullable().optional(),
    includeExtensions: z.array(z.string()).optional(),
    excludeExtensions: z.array(z.string()).optional(),
    maxSizeBytes: z.number().int().positive().nullable().optional(),
    excludePathGlobs: z.array(z.string()).optional(),
  })
  .strict();

export type DriveRootFiltersInput = z.infer<typeof driveRootFiltersSchema>;

export const driveListAvailableInputSchema = z.object({}).strict();
export const driveListInputSchema = z.object({}).strict();

export const driveAddInputSchema = z
  .object({
    providerDriveId: z.string().trim().min(1),
    sourceId: z.number().int().positive(),
  })
  .strict();
export type DriveAddInput = z.infer<typeof driveAddInputSchema>;

export const driveRemoveInputSchema = z
  .object({
    driveId: z.number().int().positive(),
  })
  .strict();
export type DriveRemoveInput = z.infer<typeof driveRemoveInputSchema>;

export const driveBrowseFolderInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    parentProviderItemId: z.string().trim().min(1).nullable(),
  })
  .strict();
export type DriveBrowseFolderInput = z.infer<typeof driveBrowseFolderInputSchema>;

export const driveRootCreateInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    providerItemId: z.string().trim().min(1),
    role: z.enum(DRIVE_ROOT_ROLES),
    filters: driveRootFiltersSchema.optional(),
    backgroundExtraction: z.boolean().optional(),
  })
  .strict();
export type DriveRootCreateInput = z.infer<typeof driveRootCreateInputSchema>;
export const driveRootCreateBodySchema = driveRootCreateInputSchema.omit({ driveId: true });
export type DriveRootCreateBody = z.infer<typeof driveRootCreateBodySchema>;

export const driveRootUpdateInputSchema = z
  .object({
    rootId: z.number().int().positive(),
    filters: driveRootFiltersSchema.optional(),
    backgroundExtraction: z.boolean().optional(),
  })
  .strict();
export type DriveRootUpdateInput = z.infer<typeof driveRootUpdateInputSchema>;
export const driveRootUpdateBodySchema = driveRootUpdateInputSchema.omit({ rootId: true });
export type DriveRootUpdateBody = z.infer<typeof driveRootUpdateBodySchema>;

export const driveRootRemoveInputSchema = z
  .object({
    rootId: z.number().int().positive(),
  })
  .strict();
export type DriveRootRemoveInput = z.infer<typeof driveRootRemoveInputSchema>;

export interface DriveRootSafe {
  id: number;
  driveId: number;
  providerItemId: string;
  path: string;
  role: DriveRootRoleValue;
  modifiedAfter: string | null;
  includeExtensions: string[];
  excludeExtensions: string[];
  maxSizeBytes: number | null;
  excludePathGlobs: string[];
  backgroundExtraction: boolean;
  libraryTagGroupId: number | null;
  addedByMemberId: number;
  createdAt: string;
  updatedAt: string;
  pendingProposalCount: number;
  libraryTagName: string | null;
  libraryTagCount: number;
  textEligibleCount: number;
  textReadyCount: number;
}

export interface DriveSafe {
  id: number;
  sourceId: number;
  sourceKind: DriveSourceKindValue;
  fileProvider: FileProviderValue;
  productLabel: string;
  name: string;
  providerDriveId: string;
  webUrl: string | null;
  writable: boolean | null;
  enabled: boolean;
  lastSyncStartedAt: string | null;
  lastSyncFinishedAt: string | null;
  lastSyncError: string | null;
  addedByMemberId: number;
  createdAt: string;
  updatedAt: string;
  roots: DriveRootSafe[];
  referenceCount: number;
  lastRun: DriveSyncRunSafe | null;
}

export interface DriveListResult {
  drives: DriveSafe[];
}

export interface AvailableDrive extends NormalizedDrive {
  sourceId: number;
  sourceKind: DriveSourceKindValue;
  alreadyAdded: boolean;
}

export interface AvailableDriveListResult {
  drives: AvailableDrive[];
}

export interface DriveBrowseFolderResult {
  folders: NormalizedDriveItem[];
}

export interface DriveRemoveResult {
  removed: true;
  tombstoned: number;
}

export interface DriveRootRemoveResult {
  removed: true;
  tombstoned: number;
}

export const DRIVE_SYNC_PHASES = ['QUEUED', 'ENUMERATING', 'APPLYING', 'DONE', 'FAILED'] as const;
export type DriveSyncPhaseValue = (typeof DRIVE_SYNC_PHASES)[number];

export const DRIVE_SYNC_REASONS = ['SCHEDULED', 'MANUAL'] as const;
export type DriveSyncReasonValue = (typeof DRIVE_SYNC_REASONS)[number];

export const DRIVE_SYNC_IN_FLIGHT_PHASES = ['QUEUED', 'ENUMERATING', 'APPLYING'] as const;

export interface DriveSyncRunSafe {
  id: number;
  driveId: number;
  reason: DriveSyncReasonValue;
  phase: DriveSyncPhaseValue;
  processRunContentId: number | null;
  startedAt: string;
  finishedAt: string | null;
  updatedAt: string;
  pagesFetched: number;
  itemsSeen: number;
  itemsMatched: number;
  created: number;
  updated: number;
  deleted: number;
  skippedByFilter: number;
  skippedOutsideRoots: number;
  skippedFolders: number;
  filed: number;
  retagged: number;
  awaitingApproval: number;
  unfiledAtRoot: number;
  totalItemsHint: number | null;
  fullEnumeration: boolean;
  error: string | null;
}

export interface DriveSyncStartResult {
  run: DriveSyncRunSafe;
}

export interface DriveSyncCurrentResult {
  run: DriveSyncRunSafe | null;
}

export interface DriveSyncRunListResult {
  runs: DriveSyncRunSafe[];
}

export const driveSyncNowInputSchema = z
  .object({
    driveId: z.number().int().positive(),
  })
  .strict();
export type DriveSyncNowInput = z.infer<typeof driveSyncNowInputSchema>;

export const driveResyncInputSchema = z
  .object({
    driveId: z.number().int().positive(),
  })
  .strict();
export type DriveResyncInput = z.infer<typeof driveResyncInputSchema>;

export const driveSyncRunSafeSchema = z.object({
  id: z.number().int(),
  driveId: z.number().int(),
  reason: z.enum(DRIVE_SYNC_REASONS),
  phase: z.enum(DRIVE_SYNC_PHASES),
  processRunContentId: z.number().int().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  updatedAt: z.string(),
  pagesFetched: z.number().int(),
  itemsSeen: z.number().int(),
  itemsMatched: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  deleted: z.number().int(),
  skippedByFilter: z.number().int(),
  skippedOutsideRoots: z.number().int(),
  skippedFolders: z.number().int(),
  filed: z.number().int(),
  retagged: z.number().int(),
  awaitingApproval: z.number().int(),
  unfiledAtRoot: z.number().int(),
  totalItemsHint: z.number().int().nullable(),
  fullEnumeration: z.boolean(),
  error: z.string().nullable(),
});

export const driveSyncStartOutputSchema = z.object({ run: driveSyncRunSafeSchema });

export const driveRootSafeSchema = z.object({
  id: z.number().int(),
  driveId: z.number().int(),
  providerItemId: z.string(),
  path: z.string(),
  role: z.enum(DRIVE_ROOT_ROLES),
  modifiedAfter: z.string().nullable(),
  includeExtensions: z.array(z.string()),
  excludeExtensions: z.array(z.string()),
  maxSizeBytes: z.number().int().nullable(),
  excludePathGlobs: z.array(z.string()),
  backgroundExtraction: z.boolean(),
  libraryTagGroupId: z.number().int().nullable(),
  addedByMemberId: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  pendingProposalCount: z.number().int(),
  libraryTagName: z.string().nullable(),
  libraryTagCount: z.number().int(),
  textEligibleCount: z.number().int().nonnegative(),
  textReadyCount: z.number().int().nonnegative(),
});

export const driveSafeSchema = z.object({
  id: z.number().int(),
  sourceId: z.number().int(),
  sourceKind: z.enum(DRIVE_SOURCE_KINDS),
  fileProvider: z.enum(FILE_PROVIDERS),
  productLabel: z.string(),
  name: z.string(),
  providerDriveId: z.string(),
  webUrl: z.string().nullable(),
  writable: z.boolean().nullable(),
  enabled: z.boolean(),
  lastSyncStartedAt: z.string().nullable(),
  lastSyncFinishedAt: z.string().nullable(),
  lastSyncError: z.string().nullable(),
  addedByMemberId: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  roots: z.array(driveRootSafeSchema),
  referenceCount: z.number().int(),
  lastRun: driveSyncRunSafeSchema.nullable(),
});

export const driveListOutputSchema = z.object({ drives: z.array(driveSafeSchema) });

export const availableDriveSchema = z.object({
  providerDriveId: z.string(),
  name: z.string(),
  siteName: z.string().nullable(),
  fileProvider: z.enum(['SHAREPOINT', 'SERVER_SHARE', 'GOOGLE_DRIVE']),
  productLabel: z.string(),
  webUrl: z.string().nullable(),
  sourceId: z.number().int(),
  sourceKind: z.enum(DRIVE_SOURCE_KINDS),
  alreadyAdded: z.boolean(),
});

export const driveListAvailableOutputSchema = z.object({ drives: z.array(availableDriveSchema) });

export const normalizedDriveItemSchema = z.object({
  providerItemId: z.string(),
  parentProviderItemId: z.string().nullable(),
  name: z.string(),
  path: z.string(),
  isFolder: z.boolean(),
  sizeBytes: z.number().nullable(),
  mimeType: z.string().nullable(),
  fetchMimeType: z.string().nullable(),
  etag: z.string().nullable(),
  modifiedAt: z.string().nullable(),
  webUrl: z.string().nullable(),
});

export const driveBrowseFolderOutputSchema = z.object({ folders: z.array(normalizedDriveItemSchema) });

export const driveRemoveOutputSchema = z.object({ removed: z.literal(true), tombstoned: z.number().int() });
export const driveRootRemoveOutputSchema = z.object({ removed: z.literal(true), tombstoned: z.number().int() });

export const DRIVE_ANCHOR_PROPOSAL_KINDS = ['LINK_EXISTING', 'CREATE_NEW'] as const;
export type DriveAnchorProposalKindValue = (typeof DRIVE_ANCHOR_PROPOSAL_KINDS)[number];

export const DRIVE_ANCHOR_PROPOSAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type DriveAnchorProposalStatusValue = (typeof DRIVE_ANCHOR_PROPOSAL_STATUSES)[number];

export const DRIVE_ANCHOR_DECISIONS = ['LINK', 'CREATE', 'REJECT'] as const;
export type DriveAnchorDecisionValue = (typeof DRIVE_ANCHOR_DECISIONS)[number];

export interface DriveAnchorProposalSafe {
  id: number;
  rootId: number;
  driveId: number;
  providerItemId: string;
  folderName: string;
  path: string;
  proposal: DriveAnchorProposalKindValue;
  matchedAnchorId: number | null;
  matchedAnchorTitle: string | null;
  status: DriveAnchorProposalStatusValue;
  decidedByMemberId: number | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface DriveAnchorProposalListResult {
  proposals: DriveAnchorProposalSafe[];
  pendingCount: number;
}

export const driveAnchorProposalsListInputSchema = z
  .object({
    rootId: z.number().int().positive().optional(),
  })
  .strict();
export type DriveAnchorProposalsListInput = z.infer<typeof driveAnchorProposalsListInputSchema>;

export const driveAnchorDecisionsInputSchema = z
  .object({
    decisions: z
      .array(
        z
          .object({
            proposalId: z.number().int().positive(),
            decision: z.enum(DRIVE_ANCHOR_DECISIONS),
            anchorId: z.number().int().positive().optional(),
          })
          .strict()
      )
      .min(1)
      .max(200),
  })
  .strict();
export type DriveAnchorDecisionsInput = z.infer<typeof driveAnchorDecisionsInputSchema>;

export interface DriveAnchorDecisionsResult {
  decided: DriveAnchorProposalSafe[];
  filed: number;
}

export const driveAnchorProposalReopenInputSchema = z
  .object({
    proposalId: z.number().int().positive(),
  })
  .strict();

export const driveApplyRootsInputSchema = z
  .object({
    driveId: z.number().int().positive(),
  })
  .strict();

export interface DriveApplyRootsResult {
  filed: number;
  retagged: number;
  awaitingApproval: number;
  unfiledAtRoot: number;
  proposalsCreated: number;
}

export const driveAnchorProposalSafeSchema = z.object({
  id: z.number().int(),
  rootId: z.number().int(),
  driveId: z.number().int(),
  providerItemId: z.string(),
  folderName: z.string(),
  path: z.string(),
  proposal: z.enum(DRIVE_ANCHOR_PROPOSAL_KINDS),
  matchedAnchorId: z.number().int().nullable(),
  matchedAnchorTitle: z.string().nullable(),
  status: z.enum(DRIVE_ANCHOR_PROPOSAL_STATUSES),
  decidedByMemberId: z.number().int().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const driveAnchorProposalListOutputSchema = z.object({
  proposals: z.array(driveAnchorProposalSafeSchema),
  pendingCount: z.number().int(),
});

export const driveAnchorDecisionsOutputSchema = z.object({
  decided: z.array(driveAnchorProposalSafeSchema),
  filed: z.number().int(),
});

export const driveApplyRootsOutputSchema = z.object({
  filed: z.number().int(),
  retagged: z.number().int(),
  awaitingApproval: z.number().int(),
  unfiledAtRoot: z.number().int(),
  proposalsCreated: z.number().int(),
});

export {
  DRIVE_TEXT_EXTRACTION_CADENCE_MINUTES,
  DRIVE_TEXT_EXTRACTION_MAX_BYTES,
  DRIVE_TEXT_EXTRACTION_MAX_FILES,
} from '../logic/drives/text-constants.js';

export interface DriveFileHit {
  contentId: number;
  code: string;
  title: string;
  path: string;
  driveId: number;
  driveName: string;
  webUrl: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  modifiedAt: string | null;
}

export const DRIVE_SEARCH_SOURCES = ['name', 'text', 'provider'] as const;
export type DriveSearchSource = (typeof DRIVE_SEARCH_SOURCES)[number];

export interface DriveSearchHit extends DriveFileHit {
  source: DriveSearchSource;
  snippet: string | null;
  locator: SearchMatch['locator'];
  locatorLabel: string | null;
}

export const driveFileHitSchema = z.object({
  contentId: z.number().int(),
  code: z.string(),
  title: z.string(),
  path: z.string(),
  driveId: z.number().int(),
  driveName: z.string(),
  webUrl: z.string().nullable(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  modifiedAt: z.string().nullable(),
});

export const driveSearchHitSchema = driveFileHitSchema.extend({
  source: z.enum(DRIVE_SEARCH_SOURCES),
  snippet: z.string().nullable().describe('Matched text with [[ ]] around the matched words (text hits only).'),
  locator: jsonValueSchema.nullable(),
  locatorLabel: z.string().nullable().describe('Where the text hit landed, e.g. "page 4" (text hits only).'),
});

export const driveBrowseInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    path: z.string().min(1).default('/'),
  })
  .strict();
export type DriveBrowseInput = z.infer<typeof driveBrowseInputSchema>;

export interface DriveBrowseResult {
  driveId: number;
  path: string;
  folders: string[];
  files: DriveFileHit[];
}

export const driveBrowseOutputSchema = z.object({
  driveId: z.number().int(),
  path: z.string(),
  folders: z.array(z.string()),
  files: z.array(driveFileHitSchema),
});

export const driveFindInputSchema = z
  .object({
    driveId: z.number().int().positive().optional(),
    nameGlob: z.string().trim().min(1).optional(),
    pathPrefix: z.string().trim().min(1).optional(),
    extension: z.string().trim().min(1).optional(),
    modifiedAfter: z.string().datetime().optional(),
    anchorCode: z.string().trim().min(1).optional(),
    tagId: z.number().int().positive().optional(),
    limit: z.number().int().min(1).max(200).default(50),
  })
  .strict();
export type DriveFindInput = z.infer<typeof driveFindInputSchema>;

export interface DriveFindResult {
  files: DriveFileHit[];
  scanned: number;
  scanCapped: boolean;
}

export const driveFindOutputSchema = z.object({
  files: z.array(driveFileHitSchema),
  scanned: z.number().int().nonnegative(),
  scanCapped: z.boolean(),
});

export const driveSearchInputSchema = z
  .object({
    driveId: z.number().int().positive().optional(),
    query: z.string().trim().min(2).max(200).describe(SEARCH_QUERY_FIELD_DESCRIPTION),
    limit: z.number().int().min(1).max(50).default(20),
  })
  .strict();
export type DriveSearchInput = z.infer<typeof driveSearchInputSchema>;

export interface DriveSearchResult {
  results: DriveSearchHit[];
}

export const driveSearchOutputSchema = z.object({ results: z.array(driveSearchHitSchema) });

export const drivePinInputSchema = z
  .object({
    contentId: z.number().int().positive(),
  })
  .strict();
export type DrivePinInput = z.infer<typeof drivePinInputSchema>;

export interface DrivePinResult {
  pinnedContentId: number;
  pinnedCode: string;
  referenceContentId: number;
  title: string;
  sizeBytes: number;
}

export const drivePinOutputSchema = z.object({
  pinnedContentId: z.number().int(),
  pinnedCode: z.string(),
  referenceContentId: z.number().int(),
  title: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});

export const driveLiveListInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    path: z.string().min(1).default('/'),
  })
  .strict();
export type DriveLiveListInput = z.infer<typeof driveLiveListInputSchema>;

export interface DriveLiveEntry {
  name: string;
  path: string;
  isFolder: boolean;
  sizeBytes: number | null;
  mimeType: string | null;
  modifiedAt: string | null;
  mirroredContentId: number | null;
  providerItemId: string;
  webUrl: string | null;
  mirroredCode: string | null;
}

export const driveLiveEntrySchema = z.object({
  name: z.string(),
  path: z.string(),
  isFolder: z.boolean(),
  sizeBytes: z.number().nullable(),
  mimeType: z.string().nullable(),
  modifiedAt: z.string().nullable(),
  mirroredContentId: z.number().int().nullable(),
  providerItemId: z.string(),
  webUrl: z.string().nullable(),
  mirroredCode: z.string().nullable(),
});

export interface DriveLiveListResult {
  driveId: number;
  driveName: string;
  productLabel: string;
  path: string;
  folders: DriveLiveEntry[];
  files: DriveLiveEntry[];
}

export const driveLiveListOutputSchema = z.object({
  driveId: z.number().int(),
  driveName: z.string(),
  productLabel: z.string(),
  path: z.string(),
  folders: z.array(driveLiveEntrySchema),
  files: z.array(driveLiveEntrySchema),
});

export const driveLiveReadInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    path: z.string().min(2),
    maxChars: z.number().int().min(1).max(60_000).optional(),
    startChar: z.number().int().min(0).optional(),
  })
  .strict();
export type DriveLiveReadInput = z.infer<typeof driveLiveReadInputSchema>;

export const DRIVE_LIVE_READ_REASONS = ['OK', 'UNSUPPORTED_TYPE', 'TOO_LARGE', 'NO_TEXT_FOUND', 'NOT_FOUND', 'IS_FOLDER'] as const;
export type DriveLiveReadReason = (typeof DRIVE_LIVE_READ_REASONS)[number];

export interface DriveLiveReadResult {
  driveId: number;
  driveName: string;
  productLabel: string;
  path: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  modifiedAt: string | null;
  mirroredContentId: number | null;
  providerItemId: string | null;
  webUrl: string | null;
  mirroredCode: string | null;
  reason: DriveLiveReadReason;
  text: string | null;
  charCount: number;
  totalChars: number;
  truncated: boolean;
  externallyAuthored: true;
}

export const driveLiveReadOutputSchema = z.object({
  driveId: z.number().int(),
  driveName: z.string(),
  productLabel: z.string(),
  path: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  modifiedAt: z.string().nullable(),
  mirroredContentId: z.number().int().nullable(),
  providerItemId: z.string().nullable(),
  webUrl: z.string().nullable(),
  mirroredCode: z.string().nullable(),
  reason: z.enum(DRIVE_LIVE_READ_REASONS),
  text: z.string().nullable(),
  charCount: z.number().int().nonnegative(),
  totalChars: z.number().int().nonnegative(),
  truncated: z.boolean(),
  externallyAuthored: z.literal(true),
});

export const driveLiveFindInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    query: z.string().trim().min(2).max(200),
    limit: z.number().int().min(1).max(100).default(25),
  })
  .strict();
export type DriveLiveFindInput = z.infer<typeof driveLiveFindInputSchema>;

export interface DriveLiveFindResult {
  driveId: number;
  driveName: string;
  productLabel: string;
  entries: DriveLiveEntry[];
}

export const driveLiveFindOutputSchema = z.object({
  driveId: z.number().int(),
  driveName: z.string(),
  productLabel: z.string(),
  entries: z.array(driveLiveEntrySchema),
});

export const driveLiveReadQuerySchema = z
  .object({
    path: z.string().min(2),
    maxChars: z.coerce.number().int().min(1).max(60_000).optional(),
    startChar: z.coerce.number().int().min(0).optional(),
  })
  .strict();

export const driveLiveFindQuerySchema = z
  .object({
    query: z.string().trim().min(2).max(200),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export const DRIVE_KEEP_MAX_PATHS = 20;
export const DRIVE_KEEP_STATUSES = ['CREATED', 'ALREADY_KEPT', 'ALREADY_MIRRORED'] as const;
export type DriveKeepStatus = (typeof DRIVE_KEEP_STATUSES)[number];

const driveKeepPathsSchema = z
  .array(z.string().trim().min(1).max(1024))
  .min(1)
  .max(DRIVE_KEEP_MAX_PATHS)
  .transform((paths) => [...new Set(paths)]);

export const driveKeepBodySchema = z
  .object({
    paths: driveKeepPathsSchema,
    anchorId: z.number().int().positive().optional(),
  })
  .strict();
export type DriveKeepBody = z.infer<typeof driveKeepBodySchema>;

export const driveKeepInputSchema = z
  .object({
    driveId: z.number().int().positive(),
    paths: driveKeepPathsSchema,
    anchorId: z.number().int().positive().optional(),
  })
  .strict();
export type DriveKeepInput = z.infer<typeof driveKeepInputSchema>;

export interface KeptLiveFile {
  path: string;
  contentId: number;
  code: string;
  status: DriveKeepStatus;
  textReason: FileTextReadReason | null;
}

export interface KeepLiveFilesResult {
  driveId: number;
  driveName: string;
  kept: KeptLiveFile[];
  notFoundPaths: string[];
  folderPaths: string[];
  filedToAnchor: number;
  createdContentIds: number[];
}

export const driveKeepOutputSchema = z.object({
  driveId: z.number().int(),
  driveName: z.string(),
  kept: z.array(
    z.object({
      path: z.string(),
      contentId: z.number().int(),
      code: z.string(),
      status: z.enum(DRIVE_KEEP_STATUSES),
      textReason: z.nativeEnum(FileTextCacheReason).nullable(),
    })
  ),
  notFoundPaths: z.array(z.string()),
  folderPaths: z.array(z.string()),
  filedToAnchor: z.number().int().nonnegative(),
  createdContentIds: z.array(z.number().int()),
});

export const DRIVE_MIRRORED_REFS_MAX_PATHS = 100;

export const driveMirroredRefsBodySchema = z
  .object({
    paths: z
      .array(z.string().trim().min(1).max(1024))
      .min(1)
      .max(DRIVE_MIRRORED_REFS_MAX_PATHS)
      .transform((paths) => [...new Set(paths)]),
  })
  .strict();
export type DriveMirroredRefsBody = z.infer<typeof driveMirroredRefsBodySchema>;

export interface DriveMirroredRef {
  path: string;
  contentId: number;
  code: string;
  kept: boolean;
}

export interface DriveMirroredRefsResult {
  refs: DriveMirroredRef[];
}

export const drivePublishRunSummarySchema = z.object({
  created: z.number().int(),
  replaced: z.number().int(),
  moved: z.number().int(),
  deleted: z.number().int(),
  unchanged: z.number().int(),
  conflicts: z.number().int(),
  leftInPlace: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(z.object({ itemKey: z.string(), message: z.string() })),
  errorsTruncated: z.number().int(),
});
export type DrivePublishRunSummary = z.infer<typeof drivePublishRunSummarySchema>;

export const drivePublishTargetSafeSchema = z.object({
  id: z.number().int(),
  driveId: z.number().int(),
  driveName: z.string(),
  productLabel: z.string(),
  rootPath: z.string(),
  rootMissingAt: z.string().nullable(),
  lastRunStartedAt: z.string().nullable(),
  lastRunFinishedAt: z.string().nullable(),
  lastRunError: z.string().nullable(),
  inFlight: z.boolean(),
  createdByMemberId: z.number().int(),
  createdAt: z.string(),
  webUrl: z.string().nullable(),
});
export type DrivePublishTargetSafe = z.infer<typeof drivePublishTargetSafeSchema>;

export const drivePublishConflictSchema = z.object({
  itemKey: z.string(),
  name: z.string(),
  providerPath: z.string(),
  contentCode: z.string().nullable(),
  conflictAt: z.string().nullable(),
});
export type DrivePublishConflict = z.infer<typeof drivePublishConflictSchema>;

export const drivePublishStatusSchema = drivePublishTargetSafeSchema.extend({
  conflicts: z.array(drivePublishConflictSchema),
  counts: z.object({ published: z.number().int(), conflicts: z.number().int() }),
  lastRunSummary: drivePublishRunSummarySchema.nullable(),
});
export type DrivePublishStatus = z.infer<typeof drivePublishStatusSchema>;

export const drivePublishSetInputSchema = z
  .object({
    driveId: z.number().int().positive().describe('Drive id from drives.list'),
    parentProviderItemId: z
      .string()
      .trim()
      .min(1)
      .nullable()
      .describe("Folder to create the 'Vivport' folder in (providerItemId from drives.browseFolder); null for the drive root"),
  })
  .strict();
export type DrivePublishSetInput = z.infer<typeof drivePublishSetInputSchema>;

export const drivePublishRemoveInputSchema = z.object({}).strict();
export const drivePublishNowInputSchema = z.object({}).strict();
export const drivePublishStatusInputSchema = z.object({}).strict();

export const drivePublishRemoveOutputSchema = z.object({
  removed: z.literal(true),
  itemsLeftInDrive: z.number().int(),
});
export type DrivePublishRemoveResult = z.infer<typeof drivePublishRemoveOutputSchema>;

export const drivePublishStatusOutputSchema = z.object({ target: drivePublishStatusSchema.nullable() });
export type DrivePublishStatusResult = z.infer<typeof drivePublishStatusOutputSchema>;
